import React, { useMemo, useRef, useState, useEffect } from "react";
import { Select } from "antd";
import { isEqual } from "lodash";
import debounce from "lodash/debounce";
import NotFoundContent from "@/components/NotFoundContent";

const { Option } = Select;

export default function SelectAutoComplete({
  fetchOptions,
  parseFetchedOptions,
  debounceTimeout = 600,
  isLoading,
  options,
  renderOption,
  placeholder,
  value,
  onChange,
  defaultActiveFirstOption,
  style,
  disabled = false,
  customFilter,
  autoPopulateOptions = false,
}) {
  const [fetching, setFetching] = useState(false);
  const [currentOptions, setCurrentOptions] = useState(() => options ?? []);
  const fetchRef = useRef(0);

  useEffect(() => {
    if (options?.length > 0 && !isEqual(options, currentOptions)) {
      const newOptions = customFilter ? customFilter(options) : options;
      setCurrentOptions(newOptions);
    }
  }, [options, customFilter, currentOptions]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = value => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setCurrentOptions([]);
      setFetching(true);
      fetchOptions(value)
        .then(async response =>
          typeof parseFetchedOptions === "function" ? await parseFetchedOptions(response) : response,
        )
        .then(newOptions => {
          if (fetchId !== fetchRef.current) {
            // for fetch callback order
            return;
          }

          setCurrentOptions(newOptions?.items ?? []);
          setFetching(false);
        });
    };
    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, fetchOptions, parseFetchedOptions]);

  useEffect(() => {
    if (autoPopulateOptions) {
      debounceFetcher("");
    }
  }, [autoPopulateOptions, debounceFetcher]);

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      filterOption={false} // disable client-side filter
      onSearch={debounceFetcher}
      notFoundContent={NotFoundContent({ loading: fetching || isLoading })}
      loading={fetching || isLoading}
      style={style}
      defaultActiveFirstOption={defaultActiveFirstOption}
      disabled={disabled}
      onClear={() => {
        onChange(null);
        fetchOptions("");
      }}
      allowClear
      showSearch
    >
      {currentOptions?.map(option =>
        renderOption ? (
          renderOption(option) // custom render
        ) : (
          <Option key={option.id} value={option.value}>
            {option.label}
          </Option>
        ),
      )}
    </Select>
  );
}
